import { useEffect, useMemo, RefObject } from 'react';
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  InfiniteData,
} from '@tanstack/react-query';
import { ga } from '@/lib/analytics';

export interface UseInfiniteScrollOptions<
  TData,
  TParams,
  TResponse = TData[],
> {
  fetchFn: (
    params: TParams & { offset: number; limit: number },
  ) => Promise<TResponse>;
  section?: string;
  queryKey: readonly unknown[];
  params?: Omit<TParams, 'offset' | 'limit'>;
  scrollContainerRef?: RefObject<HTMLDivElement | HTMLElement | null>;
  useWindowScroll?: boolean;
  pageSize?: number;
  initialLimit?: number;
  scrollThreshold?: number;
  enabled?: boolean;
  autoLoadWhenNoScroll?: boolean;
  extractData?: (response: TResponse) => TData[];
  queryOptions?: Partial<
    UseInfiniteQueryOptions<
      TResponse,
      Error,
      InfiniteData<TResponse, unknown>,
      readonly unknown[],
      unknown
    >
  >;
}

export interface UseInfiniteScrollReturn<TData, TResponse = unknown> {
  data: TData[];
  isPending: boolean;
  isLoading: boolean;
  isRefetching: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  isError: boolean;
  error: Error | null;
  failureCount: number;
  fetchNextPage: () => void;
  refetch: () => void;
  pageCount: number;
  total_count?: number;
  firstPage?: TResponse;
}

export function useInfiniteScroll<
  TData,
  TParams = Record<string, unknown>,
  TResponse = TData[],
>({
  fetchFn,
  section,
  queryKey,
  params = {} as Omit<TParams, 'offset' | 'limit'>,
  scrollContainerRef,
  useWindowScroll = false,
  pageSize = 10,
  initialLimit,
  scrollThreshold = 100,
  enabled = true,
  autoLoadWhenNoScroll = true,
  extractData,
  queryOptions = {},
}: UseInfiniteScrollOptions<
  TData,
  TParams,
  TResponse
>): UseInfiniteScrollReturn<TData, TResponse> {
  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isLoading,
    isRefetching,
    isFetching,
    isError,
    error,
    failureCount,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => {
      const currentPageParam = (pageParam as number) ?? 0;
      let offset: number;
      let limit: number;

      if (currentPageParam === 0) {
        offset = 0;
        limit = initialLimit || pageSize;
      } else {
        const initLimit = initialLimit || pageSize;
        offset = initLimit + (currentPageParam - 1) * pageSize;
        limit = pageSize;
      }

      return (() => {
        if (section) {
          ga.trackLoadMore({
            section,
            page: currentPageParam,
          });
        }
        return fetchFn({
          ...params,
          offset,
          limit,
        } as TParams & { offset: number; limit: number });
      })();
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageIndex = allPages.length - 1;
      const expectedPageSize =
        pageIndex === 0 ? initialLimit || pageSize : pageSize;

      const dataArray = extractData
        ? extractData(lastPage)
        : (lastPage as TData[]);

      return dataArray?.length === expectedPageSize
        ? allPages.length
        : undefined;
    },
    initialPageParam: 0,
    enabled,
    ...queryOptions,
  });

  const data = useMemo(() => {
    const pages = queryData?.pages;
    if (!pages) return [] as TData[];

    // Skip pages that aren't arrays and items that are null or
    // undefined, so consumers never receive invalid rows.
    return pages.flatMap((page) => {
      const items = extractData
        ? extractData(page)
        : (page as TData[]);
      return Array.isArray(items)
        ? items.filter((item) => item != null)
        : [];
    });
  }, [queryData, extractData]);

  const total_count = useMemo(() => {
    const pages = queryData?.pages;
    if (!pages || pages.length === 0) return undefined;

    return (pages[0] as TResponse & { total_count?: number })
      ?.total_count;
  }, [queryData]);

  useEffect(() => {
    if (!enabled) return;

    const handleScroll = () => {
      if (isFetchingNextPage || !hasNextPage) {
        return;
      }

      if (useWindowScroll) {
        const scrollTop = window.scrollY || window.pageYOffset;
        const clientHeight = window.innerHeight;
        const scrollHeight = document.documentElement.scrollHeight;

        if (
          scrollTop + clientHeight >=
          scrollHeight - scrollThreshold
        ) {
          fetchNextPage();
        }
        return;
      }

      const container = scrollContainerRef?.current;
      if (!container) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollTop + clientHeight >=
        scrollHeight - scrollThreshold
      ) {
        fetchNextPage();
      }
    };

    if (useWindowScroll) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }

    const container = scrollContainerRef?.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () =>
        container.removeEventListener('scroll', handleScroll);
    }

    return;
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    scrollContainerRef,
    scrollThreshold,
    enabled,
    useWindowScroll,
  ]);

  useEffect(() => {
    if (
      !enabled ||
      !autoLoadWhenNoScroll ||
      isLoading ||
      isFetchingNextPage ||
      !hasNextPage
    ) {
      return;
    }

    const checkAndLoadMore = () => {
      if (!enabled) return false;

      if (useWindowScroll) {
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = window.innerHeight;
        const hasScroll = scrollHeight > clientHeight;

        if (!hasScroll && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
        return;
      }

      const container = scrollContainerRef?.current;
      if (!container) return;

      const { scrollHeight, clientHeight } = container;
      const hasScroll = scrollHeight > clientHeight;

      if (!hasScroll && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };

    const timeoutId = setTimeout(checkAndLoadMore, 100);

    return () => clearTimeout(timeoutId);
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    scrollContainerRef,
    enabled,
    autoLoadWhenNoScroll,
    useWindowScroll,
  ]);

  const pageCount = queryData?.pages?.length || 0;

  return {
    data,
    isPending,
    isLoading,
    isRefetching,
    isFetching,
    isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    isError,
    error,
    failureCount,
    fetchNextPage,
    refetch,
    pageCount,
    total_count,
    firstPage: queryData?.pages?.[0],
  };
}

export default useInfiniteScroll;
